import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import { Navbar } from '../components/Navbar';
import { PostPreview } from '../components/PostPreview';
import { filesApi, postsApi } from '../services/api';
import { useToast } from '../hooks/use-toast';
import { Button } from '../components/ui/button';
import { useClient } from '../context/ClientContext';
import {
  Copy, Download, Check, Eye, Facebook, Calendar,
  Clock, ChevronDown, ChevronUp, Send, Filter,
  Loader2, ExternalLink, Wifi, WifiOff, RefreshCw, AlertTriangle
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WORKFLOW_FILTERS = [
  { value: 'in_review', label: 'Awaiting Client' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'posted', label: 'Posted' },
  { value: 'needs_attention', label: 'Needs Attention' },
];

const APPROVAL_LABELS = {
  approved: { label: 'Client Approved', style: 'bg-emerald-100 text-emerald-700' },
  approved_with_edits: { label: 'Client Wants Edits', style: 'bg-amber-100 text-amber-800' },
  changes_requested: { label: 'Changes Requested', style: 'bg-orange-100 text-orange-700' },
  do_not_run: { label: 'Do Not Run', style: 'bg-red-100 text-red-700' },
};

function ApprovalBadge({ post }) {
  const cfg = APPROVAL_LABELS[post.approval_status] || { label: 'Awaiting Client', style: 'bg-gray-100 text-gray-600' };
  return (
    <span
      className={`text-[10px] font-bold px-1.5 py-0.5 rounded flex items-center gap-0.5 ${cfg.style}`}
      title={post.approval_note || ''}
      data-testid={`approval-badge-${post.id}`}
    >
      {post.approval_status === 'approved' && <Check className="w-2.5 h-2.5" />}
      {cfg.label}
    </span>
  );
}

function PostPublishCard({ post, onStatusChange, linkedProfiles, clientHashtags }) {
  const [copied, setCopied] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [selectedPlatforms, setSelectedPlatforms] = useState(() => {
    // Default to all linked profiles
    if (linkedProfiles?.length > 0) return [...linkedProfiles];
    return ['facebook'];
  });
  const { toast } = useToast();

  const formatForFacebook = useCallback(() => {
    let text = '';
    if (post.title) text += post.title;
    if (post.content) {
      if (text) text += '\n\n';
      text += post.content;
    }
    if (post.theme) {
      text += `\n\n#${post.theme.replace(/\s+/g, '')}`;
    }
    if (clientHashtags && clientHashtags.length > 0) {
      text += '\n\n' + clientHashtags.join(' ');
    }
    return text;
  }, [post, clientHashtags]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(formatForFacebook());
      setCopied(true);
      toast({ title: 'Copied!', description: 'Caption copied to clipboard' });
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast({ title: 'Copy failed', description: 'Could not access clipboard', variant: 'destructive' });
    }
  };

  const handleDownloadImage = async () => {
    if (!post.image_url) {
      toast({ title: 'No image', description: 'This post has no image attached', variant: 'destructive' });
      return;
    }
    setDownloading(true);
    try {
      const blobUrl = await filesApi.get(post.image_url);
      if (!blobUrl) throw new Error('Image not found');
      const a = document.createElement('a');
      a.href = blobUrl;
      const datePart = post.scheduled_date ? format(new Date(post.scheduled_date), 'MMM-d') : 'post';
      const titleSlug = (post.title || 'untitled').replace(/[^a-zA-Z0-9]+/g, '-').slice(0, 30);
      a.download = `${datePart}_${titleSlug}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      toast({ title: 'Downloading', description: 'Image download started' });
    } catch {
      toast({ title: 'Download failed', description: 'Could not download image', variant: 'destructive' });
    } finally {
      setDownloading(false);
    }
  };

  const handleMarkPosted = async () => {
    try {
      await postsApi.updateWorkflow(post.id, 'posted');
      toast({ title: 'Marked as posted', description: post.title });
      if (onStatusChange) onStatusChange();
    } catch {
      toast({ title: 'Error', description: 'Could not update status', variant: 'destructive' });
    }
  };

  const handlePublish = async (platforms = ['facebook']) => {
    setPublishing(true);
    try {
      const { data } = await axios.post(`${API}/posts/${post.id}/publish`, { platforms });
      if (data.success) {
        toast({ title: 'Published!', description: `Post sent to ${platforms.join(', ')}` });
        if (onStatusChange) onStatusChange();
      } else {
        toast({ title: 'Publish failed', description: data.error || 'Unknown error', variant: 'destructive' });
      }
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Publish failed';
      toast({ title: 'Error', description: msg, variant: 'destructive' });
    } finally {
      setPublishing(false);
    }
  };

  const canPublish = linkedProfiles && linkedProfiles.length > 0;

  const togglePlatform = (platform) => {
    setSelectedPlatforms(prev =>
      prev.includes(platform)
        ? prev.filter(p => p !== platform)
        : [...prev, platform]
    );
  };

  const PLATFORM_ICONS = {
    facebook: { label: 'Facebook', color: '#1877F2' },
    instagram: { label: 'Instagram', color: '#E4405F' },
    twitter: { label: 'X', color: '#000000' },
    linkedin: { label: 'LinkedIn', color: '#0A66C2' },
  };

  const dateStr = post.scheduled_date
    ? format(new Date(post.scheduled_date), 'EEE, MMM d, yyyy')
    : 'Unscheduled';
  
  // Build time display: scheduled time or published time
  const timeStr = post.published_at
    ? `Published ${format(new Date(post.published_at), 'h:mm a')}`
    : post.scheduled_time
      ? `${post.scheduled_time.replace(/^(\d{1,2}):(\d{2})$/, (_, h, m) => {
          const hour = parseInt(h);
          const ampm = hour >= 12 ? 'PM' : 'AM';
          const h12 = hour % 12 || 12;
          return `${h12}:${m} ${ampm}`;
        })}`
      : null;

  return (
    <>
      <div
        className="bg-white rounded-xl border border-gray-200 overflow-hidden transition-shadow hover:shadow-md"
        data-testid={`publish-card-${post.id}`}
      >
        {/* Card Header */}
        <div
          className="flex items-center gap-3 px-4 py-3 cursor-pointer"
          onClick={() => setExpanded(!expanded)}
        >
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <Calendar className="w-3.5 h-3.5 flex-shrink-0" style={{ color: 'var(--brand-accent)' }} />
              <span className="text-xs font-medium text-gray-500">
                {dateStr}{timeStr && <span className="text-gray-400 mx-1">·</span>}
                {timeStr && (
                  <span className={post.published_at ? 'text-green-600 font-semibold' : 'text-blue-500 font-semibold'}>
                    {timeStr}
                  </span>
                )}
              </span>
              {post.platform && post.platform !== 'general' && (
                <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-blue-50 text-blue-600 capitalize">
                  {post.platform}
                </span>
              )}
              {post.post_type && post.post_type !== 'none' && (
                <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-purple-50 text-purple-600">
                  {post.post_type}
                </span>
              )}
              {post.workflow_status === 'posted' && (
                <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-green-100 text-green-700">
                  Published
                </span>
              )}
              {['failed', 'missed'].includes(post.publish_status) && (
                <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-red-100 text-red-600 flex items-center gap-0.5" title={post.publish_error || 'Publish failed'} data-testid={`publish-badge-${post.id}`}>
                  <AlertTriangle className="w-2.5 h-2.5" /> {post.publish_status === 'missed' ? 'Missed' : 'Failed'}
                </span>
              )}
              {post.workflow_status !== 'posted' && <ApprovalBadge post={post} />}
              {post.workflow_status === 'scheduled' && post.scheduled_time && !post.publish_status && post.approval_status === 'approved' && (
                <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-blue-100 text-blue-600 flex items-center gap-0.5">
                  <Send className="w-2.5 h-2.5" /> Auto-publish
                </span>
              )}
            </div>
            <p className="text-sm font-semibold text-gray-900 truncate">{post.title || 'Untitled'}</p>
            {['failed', 'missed'].includes(post.publish_status) && post.publish_error && (
              <p className="text-[11px] text-red-500 mt-0.5" title={post.publish_error} data-testid={`publish-error-${post.id}`}>
                {post.publish_error}
              </p>
            )}
            {!['failed', 'missed', 'published'].includes(post.publish_status) && post.publish_warning && (
              <p className="text-[11px] text-amber-600 mt-0.5" data-testid={`publish-warning-${post.id}`}>
                Not publishing yet: {post.publish_warning}
              </p>
            )}
          </div>

          {/* Quick Actions */}
          <div className="flex items-center gap-1.5 flex-shrink-0" onClick={e => e.stopPropagation()}>
            {post.publish_status === 'failed' && (
              <button
                onClick={async () => {
                  try {
                    await axios.post(`${API}/posts/${post.id}/retry-publish`, {}, { withCredentials: true });
                    toast({ title: 'Re-queued', description: 'Post will be retried within 60 seconds.' });
                    if (onStatusChange) onStatusChange();
                  } catch (e) {
                    toast({ title: 'Error', description: 'Could not retry.', variant: 'destructive' });
                  }
                }}
                className="p-2 rounded-lg bg-red-50 hover:bg-red-100 text-red-500 hover:text-red-600 transition-colors"
                title="Retry auto-publish"
                data-testid={`retry-btn-${post.id}`}
              >
                <RefreshCw className="w-4 h-4" />
              </button>
            )}
            <button
              onClick={handleCopy}
              className={`p-2 rounded-lg transition-colors ${
                copied ? 'bg-green-100 text-green-600' : 'bg-gray-50 hover:bg-[#1877F2]/10 text-gray-500 hover:text-[#1877F2]'
              }`}
              title="Copy caption for Facebook"
              data-testid={`copy-btn-${post.id}`}
            >
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            </button>
            {post.image_url && (
              <button
                onClick={handleDownloadImage}
                disabled={downloading}
                className="p-2 rounded-lg bg-gray-50 hover:bg-[#1877F2]/10 text-gray-500 hover:text-[#1877F2] transition-colors disabled:opacity-50"
                title="Download image"
                data-testid={`download-btn-${post.id}`}
              >
                <Download className={`w-4 h-4 ${downloading ? 'animate-bounce' : ''}`} />
              </button>
            )}
            <button
              onClick={() => setShowPreview(true)}
              className="p-2 rounded-lg bg-gray-50 hover:bg-[#1877F2]/10 text-gray-500 hover:text-[#1877F2] transition-colors"
              title="Preview post"
              data-testid={`preview-btn-${post.id}`}
            >
              <Eye className="w-4 h-4" />
            </button>
            {post.workflow_status !== 'posted' && (
              <button
                onClick={handleMarkPosted}
                className="p-2 rounded-lg bg-green-50 hover:bg-green-100 text-green-600 transition-colors"
                title="Mark as posted"
                data-testid={`mark-posted-btn-${post.id}`}
              >
                <Check className="w-4 h-4" />
              </button>
            )}
          </div>
          {expanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
        </div>

        {/* Expanded Content */}
        {expanded && (
          <div className="px-4 pb-4 border-t border-gray-100 pt-3 space-y-3">
            {/* Caption Preview */}
            <div>
              <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1 block">Caption</label>
              <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-700 whitespace-pre-wrap font-mono text-xs">
                {formatForFacebook()}
              </div>
            </div>
            {post.visual_notes && (
              <div>
                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1 block">Visual Notes</label>
                <p className="text-xs text-gray-500">{post.visual_notes}</p>
              </div>
            )}
            {/* Large Action Buttons */}
            {canPublish && (
              <div>
                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5 block">Publish to</label>
                <div className="flex items-center gap-1.5 mb-2">
                  {linkedProfiles.map(platform => (
                    <button
                      key={platform}
                      onClick={() => togglePlatform(platform)}
                      className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border transition-colors ${
                        selectedPlatforms.includes(platform)
                          ? 'text-white border-transparent'
                          : 'bg-white text-gray-400 border-gray-200'
                      }`}
                      style={selectedPlatforms.includes(platform) ? { backgroundColor: PLATFORM_ICONS[platform]?.color || '#666' } : {}}
                      data-testid={`toggle-platform-${platform}-${post.id}`}
                    >
                      {PLATFORM_ICONS[platform]?.label || platform}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div className="flex gap-2 pt-1">
              {canPublish ? (
                <Button
                  onClick={() => handlePublish(selectedPlatforms)}
                  disabled={publishing || post.workflow_status === 'posted' || selectedPlatforms.length === 0}
                  className="flex-1 bg-[#1877F2] hover:bg-[#1877F2]/90 text-white text-xs"
                  data-testid={`publish-btn-${post.id}`}
                >
                  {publishing ? (
                    <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                  ) : (
                    <Send className="w-3.5 h-3.5 mr-1.5" />
                  )}
                  {post.workflow_status === 'posted'
                    ? 'Published'
                    : publishing
                      ? 'Publishing...'
                      : `Publish to ${selectedPlatforms.length} platform${selectedPlatforms.length !== 1 ? 's' : ''}`}
                </Button>
              ) : (
                <Button
                  onClick={handleCopy}
                  className="flex-1 bg-[#1877F2] hover:bg-[#1877F2]/90 text-white text-xs"
                  data-testid={`copy-facebook-btn-${post.id}`}
                >
                  <Facebook className="w-3.5 h-3.5 mr-1.5" />
                  {copied ? 'Copied!' : 'Copy for Facebook'}
                </Button>
              )}
              {post.image_url && (
                <Button
                  onClick={handleDownloadImage}
                  disabled={downloading}
                  variant="outline"
                  className="flex-1 border-[#1877F2]/30 text-[#1877F2] hover:bg-[#1877F2]/5 text-xs"
                  data-testid={`download-image-btn-${post.id}`}
                >
                  <Download className="w-3.5 h-3.5 mr-1.5" />
                  {downloading ? 'Downloading...' : 'Download Image'}
                </Button>
              )}
            </div>
          </div>
        )}
      </div>

      <PostPreview
        isOpen={showPreview}
        onClose={() => setShowPreview(false)}
        post={post}
      />
    </>
  );
}

export default function ReadyToPostPage() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('scheduled');
  const [ayrshareStatus, setAyrshareStatus] = useState(null);
  const { toast } = useToast();
  const { activeClient } = useClient();

  const clientHashtags = activeClient?.hashtags || [];

  // Check Ayrshare connection status
  useEffect(() => {
    async function checkAyrshare() {
      try {
        const { data } = await axios.get(`${API}/ayrshare/status`);
        setAyrshareStatus(data);
      } catch {
        setAyrshareStatus({ configured: false });
      }
    }
    checkAyrshare();
  }, []);

  const fetchPosts = useCallback(async () => {
    setLoading(true);
    try {
      const data = filter === 'needs_attention'
        ? (await axios.get(`${API}/posts/needs-attention`)).data
        : await postsApi.list();
      const filtered = data
        .filter(p => filter === 'needs_attention' || (p.workflow_status === filter && p.approval_status !== 'do_not_run'))
        .sort((a, b) => new Date(a.scheduled_date) - new Date(b.scheduled_date));
      setPosts(filtered);
    } catch {
      toast({ title: 'Error loading posts', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [filter, toast, activeClient]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  // Group posts by week
  const grouped = {};
  posts.forEach(post => {
    const d = post.scheduled_date ? new Date(post.scheduled_date) : null;
    const weekKey = d ? `Week of ${format(d, 'MMM d')}` : 'Unscheduled';
    if (!grouped[weekKey]) grouped[weekKey] = [];
    grouped[weekKey].push(post);
  });

  const handleCopyAll = async () => {
    const allText = posts.map(p => {
      let text = '';
      if (p.scheduled_date) text += format(new Date(p.scheduled_date), 'EEE, MMM d') + ':\n';
      if (p.title) text += p.title + '\n';
      if (p.content) text += p.content;
      if (clientHashtags.length > 0) {
        text += '\n\n' + clientHashtags.join(' ');
      }
      return text;
    }).join('\n\n---\n\n');

    try {
      await navigator.clipboard.writeText(allText);
      toast({ title: `Copied all ${posts.length} posts`, description: 'All captions copied to clipboard' });
    } catch {
      toast({ title: 'Copy failed', variant: 'destructive' });
    }
  };

  return (
    <div className="min-h-screen page-background" data-testid="ready-to-post-page">
      <Navbar />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2" style={{ color: 'var(--brand-primary)' }}>
              <Send className="w-6 h-6 text-[#1877F2]" />
              Publish Queue
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              {ayrshareStatus?.linked_profiles?.length > 0
                ? 'Publish directly to connected social platforms'
                : 'Copy captions and download images — ready to post'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {posts.length > 0 && (
              <Button
                onClick={handleCopyAll}
                variant="outline"
                className="border-[#1877F2]/30 text-[#1877F2] hover:bg-[#1877F2]/5"
                data-testid="copy-all-btn"
              >
                <Copy className="w-4 h-4 mr-2" />
                Copy All ({posts.length})
              </Button>
            )}
          </div>
        </div>

        {/* Ayrshare Connection Banner */}
        {ayrshareStatus && (
          <div className={`rounded-xl border p-3 mb-6 flex items-center justify-between ${
            ayrshareStatus.linked_profiles?.length > 0
              ? 'bg-green-50 border-green-200'
              : ayrshareStatus.configured
                ? 'bg-amber-50 border-amber-200'
                : 'bg-gray-50 border-gray-200'
          }`} data-testid="ayrshare-banner">
            <div className="flex items-center gap-2">
              {ayrshareStatus.linked_profiles?.length > 0 ? (
                <Wifi className="w-4 h-4 text-green-600" />
              ) : (
                <WifiOff className="w-4 h-4 text-amber-600" />
              )}
              <span className="text-sm font-medium text-gray-700">
                {ayrshareStatus.linked_profiles?.length > 0
                  ? `Connected: ${ayrshareStatus.linked_profiles.join(', ')}`
                  : ayrshareStatus.configured
                    ? 'API key set — connect your social profiles in Ayrshare'
                    : 'No publishing API configured'}
              </span>
            </div>
            {ayrshareStatus.configured && ayrshareStatus.linked_profiles?.length === 0 && (
              <a
                href="https://app.ayrshare.com/social-accounts"
                target="_blank"
                rel="noreferrer"
                className="text-xs font-semibold text-amber-700 hover:text-amber-800 flex items-center gap-1"
                data-testid="connect-profiles-link"
              >
                Connect Facebook & Instagram <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </div>
        )}

        {/* Filters */}
        <div className="flex items-center gap-2 mb-6" data-testid="publish-filters">
          <Filter className="w-4 h-4 text-gray-400" />
          {WORKFLOW_FILTERS.map(f => (
            <button
              key={f.value}
              onClick={() => setFilter(f.value)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                filter === f.value
                  ? 'bg-[#1877F2] text-white'
                  : 'bg-white text-gray-500 hover:bg-gray-100 border border-gray-200'
              }`}
              data-testid={`filter-${f.value}`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Posts */}
        {loading ? (
          <div className="text-center py-16">
            <div className="w-8 h-8 border-2 border-[#1877F2] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
            <p className="text-sm text-gray-400">Loading posts...</p>
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl border border-gray-200" data-testid="empty-state">
            <Send className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <h3 className="text-base font-semibold text-gray-500 mb-1">No {filter.replace(/_/g, ' ')} posts</h3>
            <p className="text-sm text-gray-400">
              {filter === 'needs_attention' ? 'Every scheduled post is on track to publish' : `Change a post's workflow status to "${filter.replace(/_/g, ' ')}" to see it here`}
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {Object.entries(grouped).map(([week, weekPosts]) => (
              <div key={week}>
                <div className="flex items-center gap-2 mb-2">
                  <Clock className="w-3.5 h-3.5 text-gray-400" />
                  <h2 className="text-xs font-bold text-gray-400 uppercase tracking-wider">{week}</h2>
                  <span className="text-[10px] text-gray-300">{weekPosts.length} post{weekPosts.length !== 1 ? 's' : ''}</span>
                </div>
                <div className="space-y-2">
                  {weekPosts.map(post => (
                    <PostPublishCard
                      key={post.id}
                      post={post}
                      onStatusChange={fetchPosts}
                      linkedProfiles={ayrshareStatus?.linked_profiles || []}
                      clientHashtags={clientHashtags}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
